/**
 * Is the store open? (STATES_AND_INTERACTIVE_ELEMENTS_PLAN §13.3, §13.5)
 *
 * The one resolver for every renderer and the page runtime, from a store's
 * Store Central schedule: weekly hours (`store_master_hours`) and dated
 * overrides (`store_master_holiday_hours` — closures and amended hours).
 * Framework-free with no imports; storefront copies are hand-mirrored.
 *
 * Rules:
 * - a dated override replaces that date's weekly hours;
 * - two overrides for one date (Store Central doesn't prevent it): a closure
 *   wins, else the first — a button must never say "open" on a day the store
 *   may be shut;
 * - closing at or before opening runs past midnight (18:00–02:00), and
 *   yesterday's late hours are checked too, so 01:00 Tuesday is open;
 * - times are the store's own timezone (DST included); with none, the site's;
 * - a day with no hours and no "closed" is unknown, not closed.
 */

export interface WeeklyHours {
  /** 0 = Sunday … 6 = Saturday. */
  day: number;
  open?: string | null;
  close?: string | null;
  closed?: boolean;
}

export interface DateOverride {
  /** `YYYY-MM-DD`, in the store's timezone. */
  date: string;
  open?: string | null;
  close?: string | null;
  closed?: boolean;
  name?: string | null;
}

export interface StoreSchedule {
  timezone?: string | null;
  weekly: WeeklyHours[];
  overrides?: DateOverride[];
}

export interface StoreStatus {
  /** `unknown` when the hours that matter now aren't entered. */
  state: 'open' | 'closed' | 'unknown';
  /** Epoch ms the current opening ends (open only). */
  closesAt?: number;
  /** Epoch ms of the next opening (closed only, within the next 8 days). */
  opensAt?: number;
  /** Minutes until `closesAt` / `opensAt`, rounded up. */
  minutesToClose?: number;
  minutesToOpen?: number;
  /** The override in force today, when there is one (for "Holiday hours"). */
  overrideName?: string;
}

export interface StoreStatusOptions {
  /** Used when the store has no timezone. */
  fallbackTimezone?: string;
}

interface Window {
  start: number;
  end: number;
}

const DAY_MS = 86_400_000;
const LOOK_AHEAD_DAYS = 8;

function parts(ms: number, timeZone: string): { y: number; m: number; d: number; h: number; mi: number; dow: number } {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    weekday: 'short',
    hourCycle: 'h23',
  });
  const p: Record<string, string> = {};
  for (const part of fmt.formatToParts(new Date(ms))) p[part.type] = part.value;
  const dow = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(p.weekday);
  return { y: +p.year, m: +p.month, d: +p.day, h: +p.hour % 24, mi: +p.minute, dow };
}

/** The timezone's offset from UTC at `ms`, in ms. */
function offsetAt(ms: number, timeZone: string): number {
  const p = parts(ms, timeZone);
  const asUtc = Date.UTC(p.y, p.m - 1, p.d, p.h, p.mi);
  return asUtc - Math.floor(ms / 60_000) * 60_000;
}

/** Epoch ms of a wall-clock time on a calendar date in `timeZone`. */
function wallTime(y: number, m: number, d: number, minutes: number, timeZone: string): number {
  const guess = Date.UTC(y, m - 1, d) + minutes * 60_000;
  const first = guess - offsetAt(guess, timeZone);
  // Re-check at the result: across a DST change the offset differs.
  const second = guess - offsetAt(first, timeZone);
  return second;
}

/** `'09:30'` / `'09:30:00'` → minutes after midnight; `'24:00'` → 1440. */
function minutesOf(time: string | null | undefined): number | null {
  if (!time) return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(time.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const mi = Number(m[2]);
  if (h > 24 || mi > 59 || (h === 24 && mi > 0)) return null;
  return h * 60 + mi;
}

function isoDate(y: number, m: number, d: number): string {
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** The calendar date `offset` days from y-m-d (no timezone involved). */
function addDays(y: number, m: number, d: number, offset: number): { y: number; m: number; d: number; dow: number } {
  const t = new Date(Date.UTC(y, m - 1, d) + offset * DAY_MS);
  return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate(), dow: t.getUTCDay() };
}

function overrideFor(date: string, overrides: readonly DateOverride[]): DateOverride | undefined {
  const matches = overrides.filter((o) => o.date === date);
  return matches.find((o) => o.closed) ?? matches[0];
}

/** The hours for one calendar date: a window, `closed`, or `unknown`. */
function dayWindow(
  y: number,
  m: number,
  d: number,
  dow: number,
  schedule: StoreSchedule,
  timeZone: string,
): Window | 'closed' | 'unknown' {
  const override = overrideFor(isoDate(y, m, d), schedule.overrides ?? []);
  const hours = override ?? schedule.weekly.find((w) => w.day === dow);
  if (!hours) return 'unknown';
  if (hours.closed) return 'closed';
  const open = minutesOf(hours.open);
  const close = minutesOf(hours.close);
  if (open === null || close === null) return 'unknown';
  const start = wallTime(y, m, d, open, timeZone);
  const end = close <= open
    ? (() => { const n = addDays(y, m, d, 1); return wallTime(n.y, n.m, n.d, close, timeZone); })()
    : wallTime(y, m, d, close, timeZone);
  return { start, end };
}

export function storeStatus(schedule: StoreSchedule, now: number, options: StoreStatusOptions = {}): StoreStatus {
  const timeZone = schedule.timezone || options.fallbackTimezone || 'UTC';
  const today = parts(now, timeZone);
  const overrideToday = overrideFor(isoDate(today.y, today.m, today.d), schedule.overrides ?? []);
  const named = overrideToday?.name ? { overrideName: overrideToday.name } : {};

  const windows: Window[] = [];
  let todayUnknown = false;
  for (let offset = -1; offset <= LOOK_AHEAD_DAYS; offset++) {
    const day = addDays(today.y, today.m, today.d, offset);
    const w = dayWindow(day.y, day.m, day.d, day.dow, schedule, timeZone);
    if (w === 'unknown') {
      if (offset === 0) todayUnknown = true;
      continue;
    }
    if (w !== 'closed') windows.push(w);
  }
  windows.sort((a, b) => a.start - b.start);

  const current = windows.find((w) => w.start <= now && now < w.end);
  if (current) {
    // Back-to-back windows (open until midnight, open again at midnight)
    // read as one opening.
    let end = current.end;
    for (const w of windows) if (w.start <= end && w.end > end) end = w.end;
    return { state: 'open', closesAt: end, minutesToClose: Math.ceil((end - now) / 60_000), ...named };
  }
  if (todayUnknown) return { state: 'unknown', ...named };
  const next = windows.find((w) => w.start > now);
  return next
    ? { state: 'closed', opensAt: next.start, minutesToOpen: Math.ceil((next.start - now) / 60_000), ...named }
    : { state: 'closed', ...named };
}
